<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  api,
  type FundShortlistResponse, type LevyCheckSummaryRow, type PricingSourceResponse, type ShortlistFund,
} from '../../../api/client'
import ProspectMeasures from './ProspectMeasures.vue'
import ResetCeiling from './ResetCeiling.vue'
import ProspectsHistoryPanel from './ProspectsHistoryPanel.vue'
import {
  fmt, num, extraPct, thumbCagrPct, orderedFlags, sortFunds, productionFund, gateLines as gateLinesOf, FLAG_LEGEND, closestPotText,
  type LiveContext, type SortKey,
} from './shortlistFormat'
import { pctYear } from '../../edge/edgeSourceFormat'

interface Section { key: string; title: string; funds: ShortlistFund[]; liveCount: number }

const data = ref<FundShortlistResponse | null>(null)
const levyRows = ref<LevyCheckSummaryRow[]>([])
const pricing = ref<PricingSourceResponse | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
const message = ref<string | null>(null)
const busy = ref(false)
const grouping = ref<'sector' | 'ranked'>('sector')
const sortKey = ref<SortKey | null>(null)
const sortDir = ref<'asc' | 'desc'>('desc')
const open = ref<Set<string>>(new Set())
const confirming = ref<string | null>(null)
const reloadToken = ref(0)

let seq = 0
async function load() {
  const my = ++seq
  loading.value = true
  error.value = null
  const [s, l, p] = await Promise.allSettled([api.fundShortlist(), api.fundShortlistLevyChecks(), api.pricingSource()])
  if (my !== seq) return
  if (s.status === 'fulfilled') data.value = s.value
  else { data.value = null; error.value = s.reason instanceof Error ? s.reason.message : String(s.reason) }
  levyRows.value = l.status === 'fulfilled' ? l.value : []
  pricing.value = p.status === 'fulfilled' ? p.value : null
  loading.value = false
}
onMounted(load)

async function act(fn: () => Promise<string | null>) {
  busy.value = true
  message.value = null
  try { message.value = await fn() } catch (e) { message.value = e instanceof Error ? e.message : String(e) }
  busy.value = false
}
const scoreNow = () => act(async () => { await api.fundShortlistScoreNow(); return 'Scoring started — refresh in a minute' })
const reseed = () => act(async () => {
  const r = await api.fundShortlistReseed()
  await load()
  reloadToken.value++
  return `Facts refreshed for ${r.changed} fund(s)`
})
const refresh = () => act(async () => { await load(); reloadToken.value++; return null })
async function resetCeiling(key: string) {
  if (confirming.value !== key) { confirming.value = key; return }
  confirming.value = null
  await act(async () => { await api.fundShortlistResetCeiling(key); await load(); reloadToken.value++; return 'Ceiling reset' })
}

const gate = computed(() => data.value?.gate ?? null)
const gateLines = computed(() => gateLinesOf(gate.value))

function liveCtx(f: ShortlistFund): LiveContext | undefined {
  if (f.status !== 'live') return undefined
  const pf = productionFund(f)?.toLowerCase() ?? null
  return {
    pricingRag: pricing.value?.funds?.find(p => p.fund.toLowerCase() === pf) ?? null,
    levy: levyRows.value.find(r => r.fundId.toLowerCase() === pf) ?? null,
    gate: gate.value,
  }
}

const sections = computed<Section[]>(() => {
  const d = data.value
  if (!d) return []
  if (grouping.value === 'ranked') {
    const all = sortFunds([...d.live, ...d.sectors.flatMap(s => s.funds)], 'rank', 'asc')
    return [{ key: 'ranked', title: 'Ranked list', funds: all, liveCount: 0 }]
  }
  const out: Section[] = d.sectors.map(s => {
    const live = d.live.filter(l => l.sector === s.sector)
    return { key: s.sector, title: `${s.sector} · ${s.listedCount}/${s.cap}`, funds: [...live, ...s.funds], liveCount: live.length }
  })
  const cap = d.sectors[0]?.cap ?? 5
  for (const l of d.live) {
    if (d.sectors.some(s => s.sector === l.sector)) continue
    const ex = out.find(o => o.key === l.sector)
    if (ex) { ex.funds.push(l); ex.liveCount++ }
    else out.push({ key: l.sector, title: `${l.sector} · 0/${cap}`, funds: [l], liveCount: 1 })
  }
  return out
})

// Phone cards lead with the pot score (the rank key, spec 2026-10-30 §8a); the bar is its share of the best positive score.
const maxPot = computed(() => Math.max(0, ...sections.value.flatMap(s => s.funds.map(f => num(f.potScore250) ?? 0))))
const barPct = (f: ShortlistFund) => {
  const p = num(f.potScore250)
  return p !== null && p > 0 && maxPot.value > 0 ? Math.min(100, (p / maxPot.value) * 100) : 0
}

// Live funds lead a sector until a column is sorted; then they sort with the prospects (they carry the same measures and rank).
function tableRows(s: Section): ShortlistFund[] {
  return sortKey.value ? sortFunds(s.funds, sortKey.value, sortDir.value) : s.funds
}
function sortBy(k: SortKey) {
  if (sortKey.value === k) sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  else { sortKey.value = k; sortDir.value = k === 'rank' || k === 'fundName' || k === 'sector' ? 'asc' : 'desc' }
}
function toggle(id: string) {
  const n = new Set(open.value)
  if (n.has(id)) n.delete(id); else n.add(id)
  open.value = n
}
const flagsOf = (f: ShortlistFund) => orderedFlags(f.flags ?? [])
// Server gives not-rankable rows a rank (tier 2, sorted last), so the reason, not a null rank, decides.
const rankText = (f: ShortlistFund) => (f.notRankableReason ? 'not rankable' : f.rank === null ? '—' : String(f.rank))
const edgeYear = (bp: number | null | undefined) => pctYear(num(bp))
// Closest yardstick fund shows the shortlist row's name when the response carries that fund, else its key.
const nameByKey = computed(() => {
  const d = data.value
  const m = new Map<string, string>()
  if (!d) return m
  for (const f of [...d.live, ...d.sectors.flatMap(s => s.funds), ...d.evicted, ...d.rejected, ...d.proposed]) m.set(f.fundKey, f.fundName)
  return m
})
const potFundName = (k: string) => nameByKey.value.get(k)
const closestText = (f: ShortlistFund) => closestPotText(f, potFundName)

type Col = { label: string; key?: SortKey; cell: (f: ShortlistFund) => string }
const cols: Col[] = [
  { label: 'Rank', key: 'rank', cell: rankText },
  { label: 'Fund', key: 'fundName', cell: f => f.fundName },
  { label: 'Sector', key: 'sector', cell: f => f.sector },
  { label: 'Pot score', key: 'potScore250', cell: f => fmt(f.potScore250, 2) },
  { label: 'Edge Sharpe 250d', key: 'edgeSharpe250', cell: f => fmt(f.edgeSharpe250, 2) },
  { label: 'Signal corr. with yardstick', key: 'signalCorrPot250', cell: f => fmt(f.signalCorrPot250, 2) },
  { label: 'Closest yardstick fund', cell: closestText },
  { label: 'Stale 250d %/yr', key: 'stale250Bp', cell: f => edgeYear(f.stale250Bp) },
  { label: 'Stale 120d %/yr', key: 'stale120Bp', cell: f => edgeYear(f.stale120Bp) },
  { label: 'Continuation 250d', key: 'continuation250Bp', cell: f => edgeYear(f.continuation250Bp) },
  { label: 'Drift 250d', key: 'drift250Bp', cell: f => edgeYear(f.drift250Bp) },
  { label: 'Payoff 250d', key: 'payoff250Bp', cell: f => edgeYear(f.payoff250Bp) },
  { label: 'Future', cell: f => f.future },
  { label: 'Valuation', cell: f => f.valuationTimeUk ?? '—' },
  { label: 'Edge, full history', key: 'edgeBpPerDay', cell: f => fmt(f.edgeBpPerDay, 1) },
  { label: 'Edge left %', key: 'edgeLeftPct', cell: f => fmt(f.edgeLeftPct, 1) },
  { label: 'Exp. edge %', key: 'expectedEdgePct', cell: f => fmt(f.expectedEdgePct, 1) },
  { label: 'Pass-through %', key: 'passthroughPct', cell: f => fmt(f.passthroughPct, 1) },
  { label: 'Rule CAGR %', key: 'ruleCagrPct', cell: f => fmt(f.ruleCagrPct, 1) },
  { label: 'B&H CAGR %', key: 'bhCagrPct', cell: f => fmt(f.bhCagrPct, 1) },
  { label: 'Extra', key: 'extra', cell: f => fmt(extraPct(f), 1) },
  { label: 'Thumb', key: 'thumb', cell: f => fmt(thumbCagrPct(f), 1) },
  { label: 'EL 250d %', key: 'edgeLeft250dPct', cell: f => fmt(f.edgeLeft250dPct, 1) },
  { label: 't', key: 'tStatNext', cell: f => fmt(f.tStatNext, 2) },
  { label: 'Corr', key: 'corrNext', cell: f => fmt(f.corrNext, 2) },
  { label: 'N', key: 'nDays', cell: f => fmt(f.nDays, 0) },
  { label: 'Local-ccy EL %', key: 'localCcyEdgeLeftPct', cell: f => fmt(f.localCcyEdgeLeftPct, 1) },
  { label: 'Alt future', cell: f => f.altFuture ?? '—' },
  { label: 'Alt exp. edge %', key: 'altExpectedEdgePct', cell: f => fmt(f.altExpectedEdgePct, 1) },
  { label: 'β same', key: 'betaSame', cell: f => fmt(f.betaSame, 2) },
  { label: 'β next', key: 'betaNext', cell: f => fmt(f.betaNext, 2) },
  { label: 'Flags', cell: f => flagsOf(f).join(', ') || '—' },
]
</script>

<template>
  <div class="pp" data-testid="prospects-panel">
    <header class="pp-head">
      <div v-if="!error" class="pp-asof">As of {{ data?.asOf ?? 'not yet scored' }}</div>
      <div v-if="gate" class="pp-gate">
        <span class="chip" :class="gate.pass ? 'ok' : 'bad'" data-testid="prospects-gate">{{ gate.pass ? 'Gate: pass' : `Gate: FAIL (${gateLines.length})` }}</span>
        <ul v-if="gateLines.length"><li v-for="(l, i) in gateLines" :key="i">{{ l }}</li></ul>
        <span v-if="gate.japanCorrInBand !== undefined" class="pp-note" data-testid="prospects-japan-corr">Japan correlation: {{ gate.japanFullCorr == null ? 'not computed' : gate.japanCorrInBand ? 'in band' : 'out of band' }}</span>
      </div>
      <div class="pp-actions">
        <button type="button" :disabled="busy" @click="scoreNow">Score now</button>
        <button type="button" :disabled="busy" @click="reseed">Reseed facts</button>
        <button type="button" :disabled="busy || loading" @click="refresh">Refresh</button>
      </div>
      <p v-if="message" class="pp-msg" data-testid="prospects-message">{{ message }}</p>
      <div class="pp-toggle" data-testid="prospects-grouping" role="group" aria-label="Grouping">
        <button type="button" :class="{ on: grouping === 'sector' }" :aria-pressed="grouping === 'sector'" @click="grouping = 'sector'">By sector</button>
        <button type="button" :class="{ on: grouping === 'ranked' }" :aria-pressed="grouping === 'ranked'" @click="grouping = 'ranked'">Ranked list</button>
      </div>
    </header>

    <div v-if="error" class="pp-error" data-testid="prospects-error">
      <span>{{ error }}</span> <button type="button" :disabled="loading" @click="load">Retry</button>
    </div>
    <p v-else-if="loading && !data" class="pp-note">Loading…</p>
    <!-- Spec §8b: name and fund count only; never the members (a gated member must stay unnamed). -->
    <p v-if="data?.yardstick" class="pp-yardstick" data-testid="prospects-yardstick">Ranked against {{ data.yardstick.name }} · {{ data.yardstick.fundCount }} funds</p>

    <section v-for="s in sections" :key="s.key" class="pp-sector" data-testid="prospect-sector">
      <h3>{{ s.title }}</h3>

      <div class="prospect-cards">
        <article v-for="f in s.funds" :key="f.fundKey" class="card" :data-testid="`prospect-card-${f.fundKey}`">
          <button type="button" class="card-hit" :aria-expanded="open.has('c:' + f.fundKey)" @click="toggle('c:' + f.fundKey)">
            <span class="card-top">
              <span class="rank" :title="f.notRankableReason ?? undefined">{{ rankText(f) }}</span>
              <span class="name">{{ f.fundName }}</span>
              <span v-if="f.status === 'live'" class="chip live">LIVE</span>
            </span>
            <span class="sub">{{ f.sector }}</span>
            <span class="bp" data-testid="card-pot"><span>Pot score {{ fmt(f.potScore250, 2) }}</span><span class="bar"><i :style="{ width: barPct(f) + '%' }" /></span></span>
            <span v-if="f.notRankableReason" class="sub nr" data-testid="card-not-rankable">Not rankable: {{ f.notRankableReason }}</span>
            <span class="sub">{{ fmt(f.edgeBpPerDay, 1) }} bp/day · t {{ fmt(f.tStatNext, 2) }} · Extra {{ fmt(extraPct(f), 1) }}</span>
            <span class="flags"><span v-for="fl in flagsOf(f)" :key="fl" class="chip flag" :title="FLAG_LEGEND[fl]">{{ fl }}</span></span>
          </button>
          <div v-if="open.has('c:' + f.fundKey)" class="expand">
            <ProspectMeasures :fund="f" :live="liveCtx(f)" :name-of="potFundName" />
            <ResetCeiling v-if="f.pricingCeiling != null" :confirming="confirming === f.fundKey" :busy="busy" @reset="resetCeiling(f.fundKey)" @cancel="confirming = null" />
          </div>
        </article>
      </div>

      <div class="prospect-table">
        <div class="scroll">
          <table>
            <thead>
              <tr>
                <th class="tog" />
                <th v-for="c in cols" :key="c.label" :class="{ 'fund-col': c.label === 'Fund' }" :aria-sort="c.key ? (sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined">
                  <button v-if="c.key" type="button" class="sort" :data-testid="`sort-${c.key}`" @click="sortBy(c.key)">
                    {{ c.label }}<span v-if="sortKey === c.key">{{ sortDir === 'asc' ? ' ▲' : ' ▼' }}</span>
                  </button>
                  <template v-else>{{ c.label }}</template>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="f in tableRows(s)" :key="f.fundKey">
                <tr :data-testid="`prospect-row-${f.fundKey}`">
                  <td class="tog">
                    <button type="button" :data-testid="`prospect-row-toggle-${f.fundKey}`" :aria-expanded="open.has('t:' + f.fundKey)" aria-label="Toggle details" @click="toggle('t:' + f.fundKey)">{{ open.has('t:' + f.fundKey) ? '−' : '+' }}</button>
                  </td>
                  <td v-for="c in cols" :key="c.label" :class="{ 'fund-col': c.label === 'Fund' }" :data-testid="c.label === 'Rank' ? 'rank-cell' : undefined" :title="c.label === 'Rank' ? (f.notRankableReason ?? undefined) : undefined">
                    {{ c.cell(f) }}<span v-if="c.label === 'Fund' && f.status === 'live'" class="chip live">LIVE</span>
                  </td>
                </tr>
                <tr v-if="open.has('t:' + f.fundKey)">
                  <td :colspan="cols.length + 1" class="expand">
                    <ProspectMeasures :fund="f" :live="liveCtx(f)" :name-of="potFundName" />
                    <ResetCeiling v-if="f.pricingCeiling != null" :confirming="confirming === f.fundKey" :busy="busy" @reset="resetCeiling(f.fundKey)" @cancel="confirming = null" />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </section>
    <ProspectsHistoryPanel :evicted="data?.evicted ?? []" :rejected="data?.rejected ?? []" :proposed="data?.proposed ?? []" :reload-token="reloadToken" />
  </div>
</template>

<style scoped>
.pp { color: #c9d1d9; min-width: 0; }
.pp-head { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 0.75rem; }
.pp-asof { color: #8b949e; }
.pp-yardstick { margin: 0 0 0.5rem; color: #8b949e; font-size: 0.85rem; }
.pp-gate ul { margin: 0.3rem 0 0; padding-left: 1.1rem; overflow-wrap: anywhere; }
.pp-note, .pp-msg { color: #8b949e; margin: 0; overflow-wrap: anywhere; }
.pp-actions, .pp-toggle { display: flex; flex-wrap: wrap; gap: 0.5rem; }
button { min-height: 44px; min-width: 44px; padding: 0 0.8rem; background: #21262d; color: #e6edf3; border: 1px solid #30363d; border-radius: 6px; cursor: pointer; }
button:focus-visible { outline: 2px solid #58a6ff; outline-offset: 2px; }
button:disabled { opacity: 0.5; cursor: default; }
.pp-toggle button.on { background: #1f6feb; border-color: #1f6feb; }
.pp-error { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; color: #f85149; }
.chip { display: inline-block; padding: 0.1rem 0.5rem; border-radius: 999px; font-size: 0.75rem; background: #21262d; }
.chip.ok { background: #1a7f37; color: #fff; }
.chip.bad { background: #da3633; color: #fff; }
.chip.live { background: #1f6feb; color: #fff; margin-left: 0.4rem; }
.pp-sector h3 { font-size: 1rem; margin: 1rem 0 0.5rem; color: #e6edf3; overflow-wrap: anywhere; }
.prospect-cards { display: flex; flex-direction: column; gap: 0.5rem; }
.prospect-table { display: none; }
.card { border: 1px solid #30363d; border-radius: 8px; background: #161b22; min-width: 0; }
.card-hit { display: block; width: 100%; text-align: left; background: transparent; border: 0; padding: 0.6rem 0.75rem; min-height: 44px; }
.card-hit > span { display: block; }
.card-hit > .card-top { display: flex; align-items: baseline; gap: 0.5rem; }
.rank { color: #8b949e; }
.name { font-weight: 600; overflow-wrap: anywhere; min-width: 0; }
.sub { color: #8b949e; font-size: 0.8rem; overflow-wrap: anywhere; }
.card-hit > .bp { display: flex; align-items: center; gap: 0.5rem; margin: 0.25rem 0; }
.bar { flex: 1; height: 6px; background: #21262d; border-radius: 3px; overflow: hidden; }
.bar i { display: block; height: 100%; background: #3fb950; }
.card-hit > .flags { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.25rem; }
.chip.flag { background: #3d2f00; color: #e3b341; }
.expand { padding: 0.5rem 0.75rem; }
.scroll { overflow-x: auto; max-width: 100%; }
table { border-collapse: collapse; width: 100%; font-size: 0.8rem; }
th, td { border-bottom: 1px solid #21262d; padding: 0.25rem 0.4rem; text-align: right; white-space: nowrap; }
td:nth-child(3), th:nth-child(3), td:nth-child(4), th:nth-child(4) { text-align: left; }
td.expand { white-space: normal; text-align: left; }
/* The table is far wider than the screen: the fund name stays pinned while the measures scroll under it. */
th.fund-col, td.fund-col { position: sticky; left: 0; z-index: 1; background: #121212; box-shadow: 1px 0 0 #30363d; }
.sort { background: transparent; border: 0; padding: 0 0.2rem; color: inherit; font-weight: 600; }
@media (min-width: 900px) {
  .prospect-cards { display: none; }
  .prospect-table { display: block; }
}
</style>
